import { AsyncPipe, DOCUMENT, isPlatformBrowser, isPlatformServer } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  QueryList,
  ViewChild,
  ViewChildren,
  inject,
  signal,
} from '@angular/core';
import { ActivatedRoute, NavigationStart, Router, RouterLink, RouterOutlet } from '@angular/router';
import { GalleryDto, GalleryFeedbackOptions, Section, TenantDto } from '@nusign/picflow-backend-types';
import {
  BehaviorSubject,
  Observable,
  Subject,
  distinctUntilChanged,
  filter,
  map,
  mergeWith,
  of,
  switchMap,
  take,
  takeUntil,
} from 'rxjs';
import { IsDarkColorPipe } from '../../../color-picker/is-dark-color.pipe';
import { GalleryCoverComponent } from '../../../gallery-shared/components/gallery-cover/gallery-cover.component';
import { GalleryTreeComponent } from '../../../gallery-shared/components/gallery-tree/gallery-tree.component';
import { MadeWithPicflowButtonComponent } from '../../../gallery-shared/components/made-with-picflow-button/made-with-picflow-button.component';
import { VideoViewerComponent } from '../../../gallery-shared/components/video-viewer/video-viewer.component';
import { Filter } from '../../../gallery-shared/interfaces/filter.interface';
import { singleFilter } from '../../../gallery-shared/lib/filter-assets';
import { loadGalleryFonts } from '../../../gallery-shared/lib/load-gallery-fonts';
import { ApprovalLimitInfoComponent } from '../../../gallery/components/approval-limit-info/approval-limit-info.component';
import { GalleryPictureComponent } from '../../../gallery/components/gallery-picture/gallery-picture.component';
import { VideoOriginPipe } from '../../../gallery/pipes/video-origin.pipe';
import { GROWTHBOOK } from '../../../growthbook';
import { GalleryAccessLevel } from '../../../lib/access-level';
import { getAllRouteData } from '../../../lib/get-all-route-data';
import { preloadFont } from '../../../lib/preload-font';
import { forkJoinSafe } from '../../../lib/safe-fork-join';
import { sleep } from '../../../lib/sleep';
import { AssetApprovalLimitService } from '../../../services/asset-approval-limit.service';
import { AssetService } from '../../../services/asset.service';
import { BrowserService } from '../../../services/browser.service';
import { GalleryService } from '../../../services/gallery.service';
import { PermissionService } from '../../../services/permission.service';
import { SectionService } from '../../../services/section.service';
import { StateService } from '../../../services/state.service';
import { StorageService } from '../../../services/storage.service';
import { GalleryDownloadMenuOptionsComponent } from '../../../shared-extra/components/gallery-download-menu-options/gallery-download-menu-options.component';
import { AvatarPipe } from '../../../shared-extra/pipes/avatar.pipe';
import { ContextMenuComponent } from '../../../shared/components/context-menu/context-menu.component';
import { HeaderComponent } from '../../../shared/components/header/header.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { TenantLogoAndContactsComponent } from '../../../shared/components/tenant-logo-and-contacts/tenant-logo-and-contacts.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { TooltipDirective } from '../../../shared/directives/tooltip.directive';
import { GalleryCoverUrlPipe } from '../../../shared/pipes/gallery-cover-url.pipe';
import { SortByPipe } from '../../../shared/pipes/sort-by.pipe';
import { TranslatePipe } from '../../../shared/pipes/translate.pipe';
import { AlbumSectionComponent } from '../../components/album-section/album-section.component';
import { AssetsSectionComponent } from '../../components/assets-section/assets-section.component';
import { EmbedSectionComponent } from '../../components/embed-section/embed-section.component';
import { TenantIconLoaderComponent } from '../../components/tenant-icon-loader/tenant-icon-loader.component';
import { TextSectionComponent } from '../../components/text-section/text-section.component';
import { ViewFilterComponent } from '../../components/view-filter/view-filter.component';
import { ViewGalleryHeaderComponent } from '../../components/view-gallery-header/view-gallery-header.component';
import { ViewProfileMenuComponent } from '../../components/view-profile-menu/view-profile-menu.component';

@Component({
  selector: 'app-view-gallery-page',
  templateUrl: './view-gallery-page.component.html',
  styleUrls: ['./view-gallery-page.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterOutlet,
    ButtonDirective,
    RouterLink,
    SvgComponent,
    ContextMenuComponent,
    GalleryTreeComponent,
    ViewProfileMenuComponent,
    GalleryDownloadMenuOptionsComponent,
    ViewFilterComponent,
    AlbumSectionComponent,
    TextSectionComponent,
    AssetsSectionComponent,
    EmbedSectionComponent,
    TooltipDirective,
    MadeWithPicflowButtonComponent,
    TenantIconLoaderComponent,
    AsyncPipe,
    SortByPipe,
    GalleryCoverUrlPipe,
    TranslatePipe,
    AvatarPipe,
    VideoViewerComponent,
    ViewGalleryHeaderComponent,
    TenantLogoAndContactsComponent,
    ApprovalLimitInfoComponent,
    AsyncPipe,
    VideoOriginPipe,
    GalleryPictureComponent,
    IsDarkColorPipe,
    GalleryCoverComponent,
    HeaderComponent,
  ],
})
export class ViewGalleryPageComponent implements OnInit, OnDestroy {
  public growthBook = inject(GROWTHBOOK);
  private galleryService = inject(GalleryService);
  private router = inject(Router);
  private browserService = inject(BrowserService);
  private route = inject(ActivatedRoute);
  public state = inject(StateService);
  private assetService = inject(AssetService);
  private sectionService = inject(SectionService);
  private platformId = inject(PLATFORM_ID);
  private check = inject(ChangeDetectorRef);
  private assetApprovalLimitService = inject(AssetApprovalLimitService);
  private permissionService = inject(PermissionService);
  private document = inject(DOCUMENT);
  private storage = inject(StorageService);

  gallery: GalleryDto;
  tenant: TenantDto;
  oldEmbedMode: boolean = this.route.snapshot.data.embedded;
  previewMode = signal(!!this.storage.get('galleryPreviewMode'));
  correctPicflowUrl = this.browserService.getCorrectPicflowUrl();
  embedSettings: { hideCover: boolean; blockAssetView: boolean } = this.oldEmbedMode
    ? {
        blockAssetView:
          this.route.snapshot.queryParams.blockAssetView === 'true' ||
          this.route.snapshot.queryParams.blockImageView === 'true',
        hideCover: this.route.snapshot.queryParams.hideCover === 'true',
      }
    : { hideCover: false, blockAssetView: false };
  onFilterChange$ = new BehaviorSubject<void>(undefined);
  sections$: Observable<Section[]> = this.route.parent.data.pipe(
    filter(() => isPlatformBrowser(this.platformId)),
    map(data => data.gallery.id),
    distinctUntilChanged(),
    mergeWith(this.onFilterChange$.pipe(map(() => this.route.parent.snapshot.data.gallery.id))),
    switchMap(galleryId => this.sectionService.getSections(galleryId)),
    switchMap(sections => {
      return forkJoinSafe(
        sections.map(section => {
          if (section.object === 'embed' && section.embed_url) return of(section);

          if (section.object === 'text') {
            if ((section.text_title && section.text_title !== '__EMPTY_TITLE') || section.text_paragraph) {
              return of(section);
            }
          }

          if (section.object === 'gallery') {
            return this.galleryService.getSectionGalleries(section.id, section.gallery).pipe(
              take(1),
              map(galleries => {
                if (galleries.length > 0) return section;
              })
            );
          }

          if (section.object === 'asset') {
            return this.assetService.getAssetsObservable(section.id, section.gallery, { ignore401Errors: true }).pipe(
              take(1),
              map(assets => {
                const filteredAssets = assets.filter(
                  asset => !this.filters.find(filter => singleFilter(filter, asset))
                );
                if (filteredAssets.length > 0) return section;
              })
            );
          }

          return of(undefined);
        })
      );
    }),
    map(sections => {
      return sections.filter(s => s);
    })
  );
  platformBrowser = false;
  parentGallery: GalleryDto;
  url: string;
  accessLevel: GalleryAccessLevel;
  filters: Filter[] = [];
  noAssetsResult: boolean;
  destroyed$ = new Subject<void>();
  approvedAssetCount$: Observable<number>;
  allowedAndEnabledFeedbackOptions: GalleryFeedbackOptions[];
  @ViewChildren(AssetsSectionComponent) assetsSections: QueryList<AssetsSectionComponent>;
  @ViewChild('header') header: ViewGalleryHeaderComponent;

  ngOnInit(): void {
    this.onRouteChange();
    this.route.parent.data.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.onRouteChange();
    });
    this.galleryService.onGalleryDeleted$.pipe(takeUntil(this.destroyed$)).subscribe(async gallery => {
      if (!gallery || !this.gallery) return;
      if (gallery.id !== this.gallery.id) return;
      this.browserService.reload(true);
    });
    this.galleryService.onGalleryUpdated$.pipe(takeUntil(this.destroyed$)).subscribe(async gallery => {
      if (!gallery || !this.gallery) return;
      if (gallery.id !== this.gallery.id) return;
      const newGallery = await this.galleryService.checkForExternalUpdate(this.gallery, gallery);
      if (newGallery) {
        const tenant = this.gallery.tenant;
        this.gallery = newGallery;
        this.gallery.tenant = tenant;
        this.check.detectChanges();
        this.calcAllowedFeedbackOptions();
      }
    });

    if (this.oldEmbedMode && isPlatformBrowser(this.platformId)) {
      // just in case the resize observer doesn't work
      sleep(1_000).then(() => this.sendHeightToParent());
      sleep(3_000).then(() => this.sendHeightToParent());
      sleep(5_000).then(() => this.sendHeightToParent());

      this.router.events.subscribe(event => {
        if (!(event instanceof NavigationStart)) return;
        const fullscreen = this.browserService.urlIsAssetUrl(event.url);
        window['frameCommsEvent']?.source.postMessage({ fullscreen, galleryId: this.gallery.id }, '*');
      });
    }
  }

  private onRouteChange() {
    const data = getAllRouteData(this.route.snapshot);
    this.accessLevel = this.oldEmbedMode ? 'view_only' : data.accessLevel;
    if (this.accessLevel === 'edit' && !this.storage.get('galleryPreviewMode')) {
      location.href = this.correctPicflowUrl + '/a/gallery/' + data.gallery.id + '?fromPublicLink=true';
    }
    this.gallery = data.gallery;
    this.calcAllowedFeedbackOptions();

    if (this.gallery) {
      this.approvedAssetCount$ = this.assetApprovalLimitService.getApprovedAssetCountForGallery(this.gallery.id);
    }

    this.tenant = this.gallery.tenant as TenantDto;
    if (isPlatformServer(this.platformId)) {
      this.preloadFonts();
      return;
    }
    this.url = window.location.href;
    console.debug(this.gallery); // Because in production the gallery is not accessible due to SSR
    this.platformBrowser = true;
    loadGalleryFonts(this.gallery, this.tenant);
  }

  private calcAllowedFeedbackOptions() {
    this.allowedAndEnabledFeedbackOptions = this.permissionService.getAllowedAndEnabledFeedbackOptions(
      this.gallery,
      this.accessLevel
    );
  }

  private sendHeightToParent() {
    window['frameCommsEvent']?.source.postMessage(
      { FrameHeight: document.body.clientHeight, galleryId: this.gallery.id },
      '*'
    );
  }

  async getDownloadSizeInKb(gallery: GalleryDto): Promise<number> {
    const assets = await this.assetService.getAssetsForGallery(gallery.id);
    return assets.reduce((t, asset) => t + asset.version_data.size / 1024, 0);
  }

  async getTotalDownloadSizeInKb(galleries: GalleryDto[]) {
    const sizes = await Promise.all(galleries.map(g => this.getDownloadSizeInKb(g)));
    return sizes.reduce((t, size) => t + size, 0);
  }

  async onFilter(filters: Filter[]) {
    this.filters = filters;
    this.onFilterChange$.next();
    this.check.detectChanges();
    await sleep(0);
    this.noAssetsResult = !this.assetsSections.some(section => section && (!section.items || !!section.items.length));
    this.check.detectChanges();
  }

  resetFilter() {
    this.header?.viewFilter?.resetFilter();
    this.check.detectChanges();
  }

  niceSite(site: string) {
    if (!site) return '';
    if (site.split('//').length === 1) return site;
    return site.split('//')[1];
  }

  preloadFonts() {
    if (!this.gallery) return;

    const fonts = [
      this.gallery.default_title_typography?.font_family,
      this.gallery.default_paragraph_typography?.font_family,
      this.gallery.cover.title_typography?.font_family,
      this.gallery.cover.paragraph_typography?.font_family,
    ].filter(Boolean);

    fonts.forEach(font => {
      preloadFont(font, this.document);
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
